import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
  IconButton,
  MenuItem,
  Select,
  Link,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useAppStateContext } from "../../state/AppContextProvider";
import { PromptConfig } from "../../common/models";
import LoadingSpinner from "../common/LoadingSpinner";

const AGENT_OPTIONS = [
  "NegotiatorAgent",
  "RiskAssessmentAgent",
  "CaviaarAgent",
  "POVAgent",
  "SimulationAgent",
  "NegotiationOneAgent",
  "SituationAssessmentAgent",
  "GradingAgent",
  "ExporterAgent",
  "DeceptionAgent"
];

const MODEL_OPTIONS = [
  "gpt4o",
  "gpt-4.1",
  "gpt-5-chat",
  "gpt-5-nano",
  "gpt-5-mini",
];

const PromptManager: React.FC = () => {
  const {
    prompts,
    savePrompt,
    updatePrompt,
    deletePrompt,
    fetchPrompts,
    initiatePrompt,
  } = useAppStateContext();

  // Form state
  const [formAgent, setFormAgent] = useState("");
  const [formTitle, setFormTitle] = useState("");
  const [formVersion, setFormVersion] = useState("");
  const [formTemp, setFormTemp] = useState("");
  const [formModel, setFormModel] = useState("");
  const [formPrompt, setFormPrompt] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isCreatingNewTitle, setIsCreatingNewTitle] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  // Add a new state to track the loading type
  const [loadingType, setLoadingType] = useState<"loading" | "saving">("loading");

  const abortController = new AbortController();

  useEffect(() => {
    setLoadingType("loading");
    setLoading(true);
    fetchPrompts(abortController.signal).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);

  // Get existing titles and versions for selected agent/title
  const agentTitles = prompts
    ?.filter(p => p.agent === formAgent)
    .map(p => p.title)
    .filter((v, i, arr) => arr.indexOf(v) === i);

  const agentVersions = prompts
    ?.filter(p => p.agent === formAgent && p.title === formTitle)
    .map(p => p.version)
    .filter((v, i, arr) => arr.indexOf(v) === i);

  // For title dropdown: show " (Active)" if the prompt is active
  const agentTitleOptions = agentTitles?.map(title => {
    const isActive = prompts?.some(
      p => p.agent === formAgent && p.title === title && p.isActive
    );
    return { title, isActive };
  });

  // For version dropdown: show " (Active)" if the prompt is active
  const agentVersionOptions = agentVersions?.map(version => {
    const isActive = prompts?.some(
      p => p.agent === formAgent && p.title === formTitle && p.version === version && p.isActive
    );
    return { version, isActive };
  });

  // Filter and sort prompts by isActive (true first), then by agent name
  const filteredPrompts = prompts
    ?.filter((prompt: PromptConfig) => {
      // Exclude playground prompts
      if (prompt.playgroundCreation === true) return false;
      const term = searchTerm.toLowerCase();
      return (
        prompt.agent?.toLowerCase().includes(term) ||
        prompt.title?.toLowerCase().includes(term) ||
        prompt.prompt?.toLowerCase().includes(term)
      );
    })
    ?.sort((a, b) => {
      // Sort by isActive (true first)
      if (a.isActive !== b.isActive) {
        return a.isActive ? -1 : 1;
      }
      // Then by agent name alphabetically
      return a.agent.localeCompare(b.agent);
    });

  // Load temp and prompt when selecting an existing version
  useEffect(() => {
    if (formAgent && formTitle && formVersion) {
      const selectedPrompt = prompts?.find(
        p => p.agent === formAgent && p.title === formTitle && p.version === formVersion
      );
      if (selectedPrompt) {
        setFormTemp(selectedPrompt.temp || "");
        setFormModel(selectedPrompt.llmModel || "");
        setFormPrompt(selectedPrompt.prompt || "");
      }
    } else {
      setFormTemp("");
      setFormModel("");
      setFormPrompt("");
    }
  }, [formAgent, formTitle, formVersion, prompts]);

  // Handle form submit (add or update)
  const handleFormSubmit = async () => {
    if (!formAgent.trim() || !formTitle.trim() || !formPrompt.trim()) return;
    setLoadingType("saving");
    setLoading(true);

    const existingPrompt = prompts?.find(
      p => p.agent === formAgent && p.title === formTitle && p.version === formVersion
    );

    const promptObj: PromptConfig = {
      id: existingPrompt?.id, // <-- Add this line
      agent: formAgent,
      title: formTitle,
      prompt: formPrompt,
      version: formVersion || "1",
      temp: formTemp,
      llmModel: formModel || undefined,
      isActive: existingPrompt?.isActive ?? false,
      playgroundCreation: false,
    };

    if (isEditing && editingId) {
      await updatePrompt(promptObj, abortController.signal);
    } else if (existingPrompt) {
      await updatePrompt(promptObj, abortController.signal);
      setEditingId(promptObj.agent + promptObj.title + promptObj.version);
      setIsEditing(true);
    } else {
      await savePrompt(promptObj, abortController.signal);
      // After save, get the latest prompt from the list
      const updatedPrompt = prompts?.find(
        p => p.agent === promptObj.agent && p.title === promptObj.title && p.version === promptObj.version
      ) || promptObj;
      setEditingId(updatedPrompt.agent + updatedPrompt.title + updatedPrompt.version);
      setFormAgent(updatedPrompt.agent);
      setFormTitle(updatedPrompt.title);
      setFormVersion(updatedPrompt.version);
      setFormTemp(updatedPrompt.temp || "");
      setFormModel(updatedPrompt.llmModel || "");
      setFormPrompt(updatedPrompt.prompt || "");
      setIsEditing(true);
      setIsCreatingNewTitle(false); // <-- Switch back to dropdown
    }

    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 2000);
    setLoading(false);
  };

  // Handle edit icon click
  const handleEditPrompt = (prompt: PromptConfig) => {
    setEditingId(prompt.agent + prompt.title + prompt.version);
    setFormAgent(prompt.agent);
    setFormTitle(prompt.title);
    setFormVersion(prompt.version);
    setFormTemp(prompt.temp || "");
    setFormModel(prompt.llmModel || "");
    setFormPrompt(prompt.prompt);
    setIsEditing(true);
    setIsCreatingNewTitle(false);
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingId(null);
    setFormAgent("");
    setFormTitle("");
    setFormVersion("");
    setFormTemp("");
    setFormPrompt("");
    setIsEditing(false);
    setIsCreatingNewTitle(false);
  };

  // Handle delete
  const handleDeletePrompt = async (prompt: PromptConfig) => {
    setLoadingType("saving");
    setLoading(true);
    await deletePrompt(prompt, abortController.signal);
    if (editingId === prompt.agent + prompt.title + prompt.version) {
      handleCancelEdit();
    }
    setLoading(false);
  };

  // Handle initiate
  const handleInitiatePrompt = async (prompt: PromptConfig) => {
    setLoadingType("saving");
    setLoading(true);
    await initiatePrompt(prompt, abortController.signal);
    setLoading(false);
  };

  // Version dropdown logic
  const handleVersionChange = (e: any) => {
    if (e.target.value === "__newver__") {
      // Generate a new version string using current date/time
      const now = new Date();
      const pad = (n: number) => n.toString().padStart(2, "0");
      const versionTimestamp =
        now.getFullYear().toString() +
        pad(now.getMonth() + 1) +
        pad(now.getDate()) +
        pad(now.getHours()) +
        pad(now.getMinutes()) +
        pad(now.getSeconds());
      const newVersion = `vv${versionTimestamp}`;
      setFormVersion(newVersion);
    } else {
      setFormVersion(e.target.value);
    }
  };

  const handleRefreshPrompts = () => {
    setLoadingType("loading");
    setLoading(true);
    fetchPrompts(abortController.signal).finally(() => setLoading(false));
  };

  useEffect(() => {
    // Only set editing if all fields are filled and a matching prompt exists
    if (
      formAgent &&
      formTitle &&
      formVersion &&
      prompts?.some(
        p => p.agent === formAgent && p.title === formTitle && p.version === formVersion
      )
    ) {
      setIsEditing(true);
    } else {
      setIsEditing(false);
    }
  }, [formAgent, formTitle, formVersion, prompts]);

  // Clear form fields
  const handleClearForm = () => {
    setEditingId(null);
    setFormAgent("");
    setFormTitle("");
    setFormVersion("");
    setFormTemp("");
    setFormPrompt("");
    setIsEditing(false);
    setIsCreatingNewTitle(false);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6">
          Manage Prompts
        </Typography>
        <Link
          component="button"
          variant="body2"
          underline="hover"
          sx={{ ml: 2, cursor: "pointer", color: "#1976d2" }}
          onClick={handleClearForm}
        >
          Clear Form
        </Link>
      </Box>
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            bgcolor: "rgba(255,255,255,0.6)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <LoadingSpinner text={loadingType === "saving" ? "Saving..." : "Loading..."} />
        </Box>
      )}
      <Stack spacing={2}>
        {/* Unified Edit/Create Form */}
        <Paper sx={{ p: 2, maxWidth: 900, margin: "0 auto" }}>
          <Typography variant="subtitle1">
            {isEditing ? "Update Prompt" : "Add New Prompt"}
          </Typography>
          <Select
            label="Agent Name"
            value={formAgent}
            onChange={e => {
              setFormAgent(e.target.value);
              setFormTitle("");
              setFormVersion("");
              setIsCreatingNewTitle(false);
            }}
            fullWidth
            displayEmpty
            sx={{ mb: 1 }}
          >
            <MenuItem value="" disabled>
              Select Agent Name
            </MenuItem>
            {AGENT_OPTIONS.map(agent => (
              <MenuItem key={agent} value={agent}>
                {agent}
              </MenuItem>
            ))}
          </Select>
          {/* Title Dropdown with "Create new..." */}
          <Select
            value={isCreatingNewTitle ? "__new__" : formTitle}
            onChange={e => {
              if (e.target.value === "__new__") {
                setIsCreatingNewTitle(true);
                setFormTitle("");
              } else {
                setIsCreatingNewTitle(false);
                setFormTitle(e.target.value);
              }
              setFormVersion("");
            }}
            fullWidth
            sx={{ mb: 1 }}
            displayEmpty
            disabled={!formAgent}
          >
            <MenuItem value="" disabled>
              Select Title
            </MenuItem>
            {(agentTitleOptions ?? []).map(({ title, isActive }) => (
              <MenuItem key={title} value={title}>
                {title} {isActive ? "(Active)" : ""}
              </MenuItem>
            ))}
            <MenuItem value="__new__">Create new...</MenuItem>
          </Select>
          {isCreatingNewTitle && (
            <TextField
              label="New Title"
              value={formTitle}
              onChange={e => setFormTitle(e.target.value)}
              fullWidth
              sx={{ mb: 1 }}

            />
          )}
          {/* Version Dropdown with "Create new version" */}
          <Select
            value={
              (formVersion && (agentVersions ?? []).includes(formVersion))
                ? formVersion
                : "__newver__"
            }
            onChange={handleVersionChange}
            fullWidth
            sx={{ mb: 1 }}
            displayEmpty
            disabled={!formTitle}
          >
            <MenuItem value="" disabled>
              Select Version
            </MenuItem>
            {(agentVersionOptions ?? []).map(({ version, isActive }) => (
              <MenuItem key={version} value={version}>
                {version} {isActive ? "(Active)" : ""}
              </MenuItem>
            ))}
            <MenuItem value="__newver__">Create new version</MenuItem>
          </Select>
          {formVersion && (agentVersions ?? []).indexOf(formVersion) === -1 && (
            <TextField
              label="New Version"
              value={formVersion}
              fullWidth
              sx={{ mb: 1 }}
              InputProps={{ readOnly: true }}
            />
          )}
          <TextField
            label="Temperature"
            value={formTemp}
            onChange={e => setFormTemp(e.target.value)}
            fullWidth
            sx={{ mb: 1 }}
          />
          <Select
            label="LLM Model"
            value={formModel}
            onChange={e => setFormModel(e.target.value)}
            fullWidth
            displayEmpty
            sx={{ mb: 1 }}
          >
            <MenuItem value="">
              <em>Default (gpt4o)</em>
            </MenuItem>
            {MODEL_OPTIONS.map(model => (
              <MenuItem key={model} value={model}>
                {model}
              </MenuItem>
            ))}
          </Select>
          <TextField
            label="Prompt"
            value={formPrompt}
            onChange={e => setFormPrompt(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            maxRows={10}
            sx={{ mb: 1 }}
          />
          <Stack direction="row" spacing={1} justifyContent="space-between">
            <Box>
              <Button
                variant="contained"
                onClick={handleFormSubmit}
                color={isEditing ? "secondary" : "primary"}
                sx={{ mr: 1 }}
              >
                {isEditing ? "Update Prompt" : "Add Prompt"}
              </Button>
              {isEditing && (
                <Button variant="outlined" color="inherit" onClick={handleCancelEdit} sx={{ mr: 1 }}>
                  Cancel
                </Button>
              )}
            </Box>
            {isEditing && (() => {
              const editingPrompt = prompts?.find(
                p => p.agent === formAgent && p.title === formTitle && p.version === formVersion
              );
              if (!editingPrompt) return null;
              return (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => !editingPrompt.isActive && handleInitiatePrompt(editingPrompt)}
                  disabled={editingPrompt.isActive}
                  sx={
                    editingPrompt.isActive
                      ? {
                        bgcolor: "#e0e0e0",
                        color: "#888",
                        borderColor: "#e0e0e0",
                        "&:hover": { bgcolor: "#e0e0e0" }
                      }
                      : {}
                  }
                >
                  Initiate
                </Button>
              );
            })()}
          </Stack>
          {showSuccess && (
            <Typography color="success.main" sx={{ mb: 1 }}>
              Prompt saved! You can now update it.
            </Typography>
          )}
        </Paper>
        {/* Search Bar */}
        <Paper sx={{ p: 2, display: "flex", alignItems: "center" }}>
          <SearchIcon sx={{ mr: 1 }} />
          <TextField
            label="Search"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            sx={{ flex: 1, mr: 2 }} // Make room for the icon
            placeholder="Search by agent, title, or prompt..."
          />
          <IconButton
            color="primary"
            sx={{ color: "#1976d2" }}
            onClick={handleRefreshPrompts}
            title="Refresh Prompt List"
          >
            <RefreshIcon />
          </IconButton>
        </Paper>
        {/* List Existing Prompts */}
        {filteredPrompts?.map((prompt: PromptConfig) => (
          <Paper
            key={prompt.agent + prompt.title + prompt.version}
            sx={{ p: 2, display: "flex", alignItems: "center", mb: 1 }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1">
                {prompt.agent} - {prompt.title} (v{prompt.version}) {prompt.isActive ? <span style={{ color: "green" }}>(Active)</span> : null}
              </Typography>
              <Typography variant="body2">
                {prompt.prompt.length > 60 ? prompt.prompt.substring(0, 60) + "..." : prompt.prompt}
              </Typography>
              <Typography variant="caption" sx={{ color: "gray" }}>
                Temp: {prompt.temp} | Model: {prompt.llmModel || "Default (gpt4o)"}
              </Typography>
              <Typography variant="caption" sx={{ color: "gray", display: 'block' }}>
                Created By: {prompt.createdBy || 'N/A'} | Modified By: {prompt.modifiedBy || 'N/A'} | Modified Date: {prompt.modifiedDate ? new Date(prompt.modifiedDate).toLocaleString() : 'N/A'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <IconButton
                onClick={() => handleEditPrompt(prompt)}
                sx={{ color: "#1976d2" }}
              >
                <EditIcon />
              </IconButton>
              <IconButton
                onClick={() => handleDeletePrompt(prompt)}
                color="error"
              >
                <DeleteIcon />
              </IconButton>
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
};

export default PromptManager;